import { Alert, Button, List, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveStaticAddress } from '../api/captive';

/**
 * THE STATIC-ADDRESS CHOICE AT REGISTRATION (customer request 2026-08-17:
 * «…so the user can choose one of them during registration»).
 *
 * Shown between the login and the automatic registration, and ONLY when the account
 * holds at least one assigned, unused static address — `EnrollmentFlow` skips the
 * step entirely otherwise, so there is no empty selector and no disabled control to
 * explain. The rows come from `GET /captive/static-addresses`, which lists the token
 * account's own reservations and nothing else.
 *
 * ⚠ DYNAMIC IS THE DEFAULT AND IS NEVER PRE-EMPTED, not even when exactly one
 * address is on offer. That is the opposite of the plan chooser one screen away,
 * which DOES auto-select a lone plan — and the difference is deliberate: activation
 * cannot proceed without a plan, so a one-item radio group there is a formality,
 * while here the alternative («обычный адрес») is a complete, correct outcome that
 * most subscribers want. Auto-selecting the only static would hand an address to
 * somebody who never asked for one, permanently and invisibly, on a screen he was
 * about to press «Продолжить» on.
 *
 * The value is a LEASE ID and not an address: this estate carries overlapping pool
 * CIDRs (D22), so an address is not unique across the table.
 *
 * ⚠ THERE IS NO `submitting` PROP, AND ITS ABSENCE IS THE HONEST STATE (removed
 * 2026-08-18, six-lens review). It had one, and the single call site passed the
 * literal `submitting={false}` — because «Продолжить» runs `runRegister`, whose FIRST
 * act is `setStep('registering')`, which unmounts this screen in favour of
 * `RegisteringScreen` and its own progress and retry. The spinner could therefore
 * never render, and a prop that cannot be true is a control a later reader believes
 * exists. If this screen ever gains a call it awaits in place, that state belongs
 * here — not in a parameter nobody can set.
 */
export function AddressChoiceScreen({
  addresses,
  selected,
  error,
  onSelect,
  onConfirm,
}: {
  addresses: CaptiveStaticAddress[];
  /** null = the dynamic default. */
  selected: number | null;
  /**
   * Why the previous attempt was refused, when the subscriber is back here because
   * the address he picked was not (or is no longer) bindable. The list has been
   * re-read by then, so what he sees is the truth rather than the stale offer.
   */
  error: string | null;
  onSelect: (leaseID: number | null) => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  // `null` cannot be a Radio value (antd treats it as "nothing selected" and the
  // dynamic row would render unchecked while it IS the choice), so the default is
  // carried as the sentinel 0 — no lease id is ever 0.
  const DYNAMIC = 0;

  return (
    <>
      <div className="portal-title">{t('addressChoice.title')}</div>
      <p className="portal-subtitle">{t('addressChoice.subtitle')}</p>
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} role="alert" />
      )}
      <Radio.Group
        style={{ display: 'block', width: '100%' }}
        value={selected ?? DYNAMIC}
        onChange={(e) => {
          const v = e.target.value as number;
          onSelect(v === DYNAMIC ? null : v);
        }}
      >
        <List
          size="small"
          bordered
          style={{ marginBottom: 16 }}
          dataSource={[null, ...addresses]}
          rowKey={(a) => (a === null ? 'dynamic' : `lease-${a.lease_id}`)}
          renderItem={(a) =>
            a === null ? (
              <List.Item onClick={() => onSelect(null)} style={{ cursor: 'pointer' }}>
                <Radio value={DYNAMIC} style={{ marginRight: 8 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div>{t('addressChoice.dynamic')}</div>
                  <div style={{ opacity: 0.6, fontSize: 12 }}>{t('addressChoice.dynamicHint')}</div>
                </div>
              </List.Item>
            ) : (
              <List.Item onClick={() => onSelect(a.lease_id)} style={{ cursor: 'pointer' }}>
                <Radio value={a.lease_id} style={{ marginRight: 8 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono">{a.address}</div>
                  <div style={{ opacity: 0.6, fontSize: 12 }}>{t('addressChoice.staticHint')}</div>
                </div>
              </List.Item>
            )
          }
        />
      </Radio.Group>
      <Button type="primary" block onClick={onConfirm}>
        {t('addressChoice.submit')}
      </Button>
    </>
  );
}
